import React, { useEffect, useState } from 'react';
import Accordion from 'b2b-ui-components-lib/lib/components/accordion';
import Checkbox from 'b2b-ui-components-lib/lib/components/checkbox';
import Input from 'b2b-ui-components-lib/lib/components/input';
import Datepicker from 'b2b-ui-components-lib/lib/components/datePicker';

import { gstinValidator, isFormValid } from '../../../../common/utils/validator';
import { getDefaultValue, keyForGSTFiles } from './formControl';
import { fetchGSTDetail, validateGstNumber } from '../../../../common/services/http.service';
import FileUpload from '../fileUpload/fileUpload';
import { fileSchemaForGSTCertificate, fileSchemaForGSTLOA, DefaultArrayGst, DefaultArrayLOA } from '../summary.constant';
import { getRange } from 'common/utils/app.utils';
import { FILE_MAX_SIZE_MSG } from 'common/constants/app.constants';

const AddressGSTDetails = ({ order, updateParentForm, setFormStatus, isOrderConfirmation }) => {
  const [accordionMetadata, setAccordionMetadata] = useState({ isValidated: false });
  const [form, setForm] = useState(getDefaultValue(order, isOrderConfirmation));
  const [isSEZ, setIsSEZ] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  const onContactNameChange = (value) =>
    setForm({ ...form, site: { ...form.site, contactName: { ...form.site.contactName, value, touched: true, errors: form.site.contactName.validator(value) } } });

  const onEmailChange = (value) => setForm({ ...form, site: { ...form.site, email: { ...form.site.email, value, touched: true, errors: form.site.email.validator(value) } } });

  const onMobileNoChange = (value) =>
    setForm({ ...form, site: { ...form.site, mobileNumber: { ...form.site.mobileNumber, value, touched: true, errors: form.site.mobileNumber.validator(value) } } });

  const isGSTFieldEmpty = (value) => {
    if (value === '' || value === null) {
      setForm({ ...form, gstin: { value, touched: true, errors: [] }, dty: '', sezSite: false, filesForGSTCerificate: null, filesForGSTLOA: null });
      setIsSEZ(false);
      return true;
    }
    return false;
  };
  const onGSTInChange = (value) => {
    value = value.trim().toUpperCase();
    if (gstinValidator(value)) {
      fetchGSTDetail(value).then(
        ({
          data: {
            result: { dty, sez }
          }
        }) => {
          let gstValue = form && form.gstin && form.gstin.value;
          setShowLoader(false);
          if (gstValue !== value) {
            setForm({ ...form, gstin: { value, touched: true, errors: [] }, sezSite: sez, dty, filesForGSTCerificate: null, filesForGSTLOA: null });
          } else {
            setForm({ ...form, gstin: { value, touched: true, errors: [] }, sezSite: sez, dty });
          }

          setIsSEZ(sez);
        },
        (err) => {
          let errMsg = err && err.response && err.response.data && err.response.data.errorMsg;
          setForm({ ...form, gstin: { value, touched: true, errors: [errMsg ? errMsg : 'Please enter valid GSTIN'] } });
          setShowLoader(false);
        }
      );
    } else {
      if (!isGSTFieldEmpty(value)) {
        setForm({ ...form, gstin: { value, touched: true, errors: ['Please enter valid GSTIN'] } });
      }
    }
  };
  const validateGSTIN = (value) => {
    value = value && value.trim().toUpperCase();
    if (gstinValidator(value)) {
      setForm({ ...form, gstin: { value, touched: true, errors: [] } });
      setShowLoader(true);
      validateGstNumber(value).then(
        ({ data: { result } }) => {
          onGSTInChange(value);
        },
        (err) => {
          let errMsg = err && err.response && err.response.data && err.response.data.errorMsg;
          setForm({ ...form, gstin: { value, touched: true, errors: [errMsg ? errMsg : 'Please enter valid GSTIN'] } });
          setShowLoader(false);
        }
      );
    } else {
      if (!isGSTFieldEmpty(value)) {
        setForm({ ...form, gstin: { value, touched: true, errors: ['Please enter valid GSTIN'] } });
      }
    }
  };

  const onFieldBlur = (formElement, keyName) => {
    const { value, errors: existingError } = formElement;
    let errors = [];
    if ((value === '' || value === null) && keyName !== 'gstin') {
      errors = ['This is a mandatory field'];
    }
    if (keyName === 'gstin') {
      setForm({ ...form, [keyName]: { ...form[keyName], value, errors: errors.concat(existingError), touched: true } });
    } else {
      setForm({ ...form, site: { ...form.site, [keyName]: { ...form.site[keyName], value, errors: errors.concat(existingError), touched: true } } });
    }
  };

  const onCheckBoxChange = (value) => {
    if (value) {
      setForm({
        ...form,
        addSiteInCharge: value,
        site: {
          ...form.site,
          contactName: { ...form.site.contactName, value: order.siteIncharge.firstName, errors: [] },
          email: { ...form.site.email, value: order.siteIncharge.email, errors: [] },
          mobileNumber: { ...form.site.mobileNumber, value: order.siteIncharge.phoneNumber, errors: [] }
        }
      });
      return;
    }
    setForm({
      ...form,
      addSiteInCharge: value,
      site: { ...form.site, contactName: { ...form.site.contactName, value: '' }, email: { ...form.site.email, value: '' }, mobileNumber: { ...form.site.mobileNumber, value: '' } }
    });
  };

  const onFileUpload = (status, type) => {
    setForm({ ...form, [type]: status });
  };

  const isSiteChecked = () => {
    const isContactNameSame = form.site?.contactName?.value === order.siteIncharge?.firstName;
    const isEmailSame = form.site?.email?.value === order.siteIncharge?.email;
    const isMobileSame = form.site?.mobileNumber?.value === order.siteIncharge?.phoneNumber;
    return isContactNameSame && isEmailSame && isMobileSame;
  };

  const onEstimatedDateChange = (value, keyName) => {
    if (value) {
      setForm({
        ...form,
        [keyName]: { ...form[keyName], value, touched: true, errors: [] }
      });
      return;
    }
    setForm({
      ...form,
      [keyName]: {
        ...form[keyName],
        value,
        touched: true,
        errors: ['This field is mandatory']
      }
    });
  };

  useEffect(() => {
    setForm((prev) => {
      const form = getDefaultValue(order);
      if (form && form.gstin && form.gstin.value) {
        validateGSTIN(form.gstin.value);
      }
      return { ...form };
    });
  }, [order]);

  useEffect(() => {
    const ignoreGSTKeys = form.gstin.value ? ['sezSite', 'dty'] : ['sezSite', 'dty', 'gstin'];
    let keyToIgnore = form.addSiteInCharge ? ignoreGSTKeys : ['addSiteInCharge'].concat(ignoreGSTKeys);
    if (!isSEZ) {
      keyToIgnore = keyToIgnore.concat([keyForGSTFiles.GST_CERTIFICATE, keyForGSTFiles.GST_LOA]);
    }

    if (isFormValid(form, keyToIgnore) && !showLoader) {
      setAccordionMetadata({ isValidated: true });
      updateParentForm({ addressGSTDetail: form });
      setFormStatus({ addressGSTDetail: true });
    } else {
      setAccordionMetadata({ isValidated: false });
      updateParentForm({ addressGSTDetail: null });
      setFormStatus({ addressGSTDetail: false });
    }
  }, [form]);

  return (
    <React.Fragment>
      <Accordion
        title="Installation Address and GST Details"
        autoScrollOnExpand={false}
        isExpanded={true}
        isValidated={accordionMetadata.isValidated}
        isEditable={accordionMetadata.isEditable}
        onEditClick={() => setAccordionMetadata(({ isEditable }) => ({ ...accordionMetadata, isEditable: !isEditable }))}
      >
        <div className="accordion_body">
          <div className="order_details_form">
            <div className="row field_row">
              <div className="flg-lg-12 flg-md-12">
                <div className="form_area">
                  <div className="row">
                    <div className="flg-lg-6 flg-md-6">
                      <div className="field_tooltip">
                        <Input label="Installation Address" required value={form.installationAddress} disabled />
                        <div className="tooltip">{form.installationAddress}</div>
                      </div>
                    </div>
                    <div className={`flg-lg-6 flg-md-6 ${isOrderConfirmation ? 'disabled' : ''}`}>
                      <div className="field_tooltip">
                        <Datepicker
                          name="datepicker"
                          label="Requested date of Delivery"
                          // disabled={isOrderConfirmation}
                          disabled={true}
                          required={true}
                          errors={form.delieveryDate.errors}
                          touched={form.delieveryDate.touched}
                          value={form.delieveryDate.value}
                          onChange={(value) => onEstimatedDateChange(value, 'delieveryDate')}
                          maxDate={new Date(new Date().setYear(new Date().getFullYear() + 1))}
                          minDate={form?.delieveryDate.estimatedDate}
                          years={getRange(new Date().getFullYear(), new Date().getFullYear() + 1)}
                        />
                      </div>
                    </div>
                    <div className="flg-lg-6 flg-md-6">
                      <div className="input_with_loader">
                        <Input
                          label="GSTIN"
                          required
                          value={form.gstin.value}
                          onChange={validateGSTIN}
                          onBlur={() => onFieldBlur(form.gstin, 'gstin')}
                          errors={form.gstin.errors}
                          touched={form.gstin.touched}
                          maxlength={15}
                          disabled={isOrderConfirmation}
                        />
                        {showLoader && <span className="loading_icon"></span>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="row field_row">
              <div className="flg-lg-12 flg-md-12">
                <div className="form_area">
                  <h6>Site Contact Details</h6>
                  <Checkbox checked={isSiteChecked()} onChange={onCheckBoxChange} disabled={isOrderConfirmation}>
                    <span className="check_text">Site contact same as contact person</span>
                  </Checkbox>

                  <div className="order_details_form">
                    <div className="row">
                      <div className="flg-lg-6 flg-md-6">
                        <div className="field_tooltip">
                          <Input
                            label="Site Contact Name"
                            required
                            value={form.site.contactName.value}
                            onChange={onContactNameChange}
                            onBlur={() => onFieldBlur(form.site.contactName, 'contactName')}
                            errors={form.site.contactName.errors}
                            touched={form.site.contactName.touched}
                            disabled={form.addSiteInCharge || isOrderConfirmation}
                          />
                          {form.addSiteInCharge && <div className="tooltip">{form.site.contactName.value}</div>}
                        </div>
                      </div>
                      <div className="flg-lg-6 flg-md-6">
                        <div className="field_tooltip">
                          <Input
                            isValidChange={(value) => /^(\s*|\d+)$/.test(value)}
                            label="Site Contact Number"
                            required
                            maxlength={10}
                            value={form.site.mobileNumber.value}
                            onChange={onMobileNoChange}
                            onBlur={() => onFieldBlur(form.site.mobileNumber, 'mobileNumber')}
                            errors={form.site.mobileNumber.errors}
                            touched={form.site.mobileNumber.touched}
                            disabled={form.addSiteInCharge || isOrderConfirmation}
                          />
                          {form.addSiteInCharge && <div className="tooltip">{form.site.mobileNumber.value}</div>}
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="flg-lg-6 flg-md-6">
                        <div className="field_tooltip">
                          <Input
                            label="Site Contact Email"
                            required
                            value={form.site.email.value}
                            onChange={onEmailChange}
                            onBlur={() => onFieldBlur(form.site.email, 'email')}
                            errors={form.site.email.errors}
                            touched={form.site.email.touched}
                            disabled={form.addSiteInCharge || isOrderConfirmation}
                          />
                          {form.addSiteInCharge && <div className="tooltip">{form.site.email.value}</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="accordion_footer">
          {isSEZ && (
            <React.Fragment>
              <FileUpload
                onFileUploadSuccess={onFileUpload}
                label="Upload GST certificate Document"
                supportedLabel="Supported file format is .PDF, .PNG, .JPG"
                limitLabel={FILE_MAX_SIZE_MSG}
                fileSchema={fileSchemaForGSTCertificate}
                type={keyForGSTFiles.GST_CERTIFICATE}
                isFileHaveValue={form && form.filesForGSTCerificate && form.filesForGSTCerificate.length > 0 ? true : false}
                fileArray={form && form.filesForGSTCerificate && form.filesForGSTCerificate.length > 0 ? form.filesForGSTCerificate : null}
                isOrderConfirmation={isOrderConfirmation}
              ></FileUpload>

              <FileUpload
                onFileUploadSuccess={onFileUpload}
                label="Upload SEZ Site LOA Document"
                supportedLabel="Supported file format is .PDF, .PNG, .JPG"
                limitLabel={FILE_MAX_SIZE_MSG}
                fileSchema={fileSchemaForGSTLOA}
                multiple={true}
                type={keyForGSTFiles.GST_LOA}
                isFileHaveValue={form && form.filesForGSTLOA && form.filesForGSTLOA.length > 0 ? true : false}
                fileArray={form && form.filesForGSTLOA && form.filesForGSTLOA.length > 0 ? form.filesForGSTLOA : null}
                isOrderConfirmation={isOrderConfirmation}
              ></FileUpload>
            </React.Fragment>
          )}
        </div>
      </Accordion>
    </React.Fragment>
  );
};

export default AddressGSTDetails;
